import type { ICampaign } from '../../../redux/buzzbridge/types';
import type { IUgcCampaignFeeConfig } from '../../../types/global/ugcCampaignConfig.types';
import { computeCampaignFeeBreakdown } from '../../../constants/campaignBudgetFees';

export type UgcFundBreakdownLine = {
    label: string;
    amountUsd: number;
    emphasis?: boolean;
    muted?: boolean;
};

export type UgcCampaignFundSummary = {
    escrowLockCents: number;
    hostFeeCents: number;
    requiredWalletCents: number;
    shortfallCents: number;
    lines: UgcFundBreakdownLine[];
    isPaidCampaign: boolean;
    hostMinPayoutSubscribeToSavePerCreatorUsd?: number | null;
};

function formatUsdFromDollars(dollars: number): number {
    return Math.round(dollars * 100) / 100;
}

export function buildUgcCampaignFundSummary(
    campaign: ICampaign,
    availableWalletCents: number,
    opts?: {
        ugcFeeConfig?: Partial<IUgcCampaignFeeConfig>;
        perCampaignFeeCents?: number;
        hasActivePlan?: boolean;
    },
): UgcCampaignFundSummary {
    const isCommissionCampaign = campaign.campaign_type === 'commission';
    const isExternalCommission = isCommissionCampaign && (campaign as any).commission?.target_type === 'external';
    const isAppInstall = Boolean((campaign as any).app_install?.enabled);
    const reward = campaign.reward;
    const isPaidCampaign = reward === 'paid' || reward === 'both';

    // Commission campaigns: escrow amount depends on type
    // External: pool + base pay × seats (both locked)
    // Non-external with base pay: base pay × seats only
    // Non-external without base pay: 0 (display pool is marketing only)
    let escrowLockCents: number;
    if (isExternalCommission) {
        const poolCents = Math.round(Number((campaign as any).commission?.display_pool_cents) || 0);
        const baseCents = Math.round(Number(campaign.base_payment_per_creator_cents) || 0);
        const seats = Math.max(1, Number(campaign.total_participants_count) || 1);
        escrowLockCents = poolCents + baseCents * seats;
    } else if (isCommissionCampaign) {
        const baseCents = Math.round(Number(campaign.base_payment_per_creator_cents) || 0);
        const seats = Math.max(1, Number(campaign.total_participants_count) || 1);
        escrowLockCents = baseCents > 0 ? baseCents * seats : 0;
    } else if (isAppInstall) {
        const cpaCents = Math.round((Number(campaign.total_budget) || 0) * 100);
        const baseCents = Math.round(Number(campaign.base_payment_per_creator_cents) || 0);
        const seats = Math.max(1, Number(campaign.total_participants_count) || 1);
        escrowLockCents = cpaCents + baseCents * seats;
    } else {
        escrowLockCents = Math.round((Number(campaign.total_budget) || 0) * 100);
    }

    const lines: UgcFundBreakdownLine[] = [];
    let hostMinPayoutSubscribeToSavePerCreatorUsd: number | null = null;
    const hostMinFromApi = campaign.fee_breakdown?.host_creator_minimum_payout;

    if (isExternalCommission) {
        const poolCents = Math.round(Number((campaign as any).commission?.display_pool_cents) || 0);
        const baseCents = Math.round(Number(campaign.base_payment_per_creator_cents) || 0);
        const seats = Math.max(1, Number(campaign.total_participants_count) || 1);
        lines.push({
            label: 'Commission pool (funds creator payouts from sales)',
            amountUsd: formatUsdFromDollars(poolCents / 100),
            emphasis: true,
        });
        if (baseCents > 0) {
            lines.push({
                label: `Base pay (${seats} creator${seats !== 1 ? 's' : ''} × $${(baseCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`,
                amountUsd: formatUsdFromDollars((baseCents * seats) / 100),
                emphasis: true,
            });
        }
    } else if (isCommissionCampaign) {
        const baseCents = Math.round(Number(campaign.base_payment_per_creator_cents) || 0);
        const seats = Math.max(1, Number(campaign.total_participants_count) || 1);
        if (baseCents > 0) {
            const baseUsd = baseCents / 100;
            lines.push({
                label: `Base pay commitment (${seats} creator${seats !== 1 ? 's' : ''} × $${baseUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`,
                amountUsd: formatUsdFromDollars((baseCents * seats) / 100),
                emphasis: true,
            });
            const displayPoolUsd = Number(campaign.commission?.display_pool_cents ?? 0) / 100 || Number(campaign.total_budget) || 0;
            if (displayPoolUsd > 0) {
                lines.push({
                    label: 'Commission display pool (marketing only — not locked)',
                    amountUsd: formatUsdFromDollars(displayPoolUsd),
                    muted: true,
                });
            }
        } else {
            lines.push({
                label: 'Commission display pool (marketing only — no wallet lock required)',
                amountUsd: formatUsdFromDollars(Number(campaign.total_budget) || 0),
                muted: true,
            });
        }
    } else if (isAppInstall) {
        const cpaCents = Math.round((Number(campaign.total_budget) || 0) * 100);
        const baseCents = Math.round(Number(campaign.base_payment_per_creator_cents) || 0);
        const seats = Math.max(1, Number(campaign.total_participants_count) || 1);
        lines.push({
            label: 'CPA budget (install / visit payouts)',
            amountUsd: formatUsdFromDollars(cpaCents / 100),
            emphasis: true,
        });
        if (baseCents > 0) {
            lines.push({
                label: `Base pay (${seats} creator${seats !== 1 ? 's' : ''} × $${(baseCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`,
                amountUsd: formatUsdFromDollars((baseCents * seats) / 100),
                emphasis: true,
            });
        }
        lines.push({
            label: 'Campaign budget (escrow lock)',
            amountUsd: formatUsdFromDollars((cpaCents + baseCents * seats) / 100),
            emphasis: true,
        });
    } else if (isPaidCampaign && (campaign.platforms?.length ?? 0) > 0) {
        const breakdown = computeCampaignFeeBreakdown(
            {
                platforms: campaign.platforms ?? [],
                participantsCount: campaign.participants_count ?? 1,
                influencerTier: campaign.influencer_tier || 'micro',
                totalBudget: Number(campaign.total_budget) || 0,
                hostHasActiveSubscription: opts?.hasActivePlan,
            },
            opts?.ugcFeeConfig,
        );

        const minPerCreator = hostMinFromApi?.applied?.min_payout_per_creator_usd ?? breakdown.minimumPerParticipant;
        const creatorPool = formatUsdFromDollars(minPerCreator * breakdown.participantsUsed);
        lines.push({
            label: `Creator pool (${breakdown.participantsUsed} × ${formatUsdFromDollars(minPerCreator)})`,
            amountUsd: creatorPool,
        });

        const platformFeeUsd = hostMinFromApi?.total_platform_fees_usd ?? breakdown.totalPlatformFee;
        if (platformFeeUsd > 0) {
            lines.push({
                label: `Platform fees (${(campaign.platforms ?? []).length} platform${
                    (campaign.platforms ?? []).length === 1 ? '' : 's'
                })`,
                amountUsd: formatUsdFromDollars(platformFeeUsd),
                muted: true,
            });
        }

        hostMinPayoutSubscribeToSavePerCreatorUsd =
            hostMinFromApi?.subscribe_to_save_per_creator_usd ??
            breakdown.hostMinPayoutSubscribeToSavePerCreatorUsd ??
            null;

        lines.push({
            label: 'Campaign budget (escrow lock)',
            amountUsd: formatUsdFromDollars(Number(campaign.total_budget) || 0),
            emphasis: true,
        });
    } else {
        lines.push({
            label: 'Campaign budget (escrow lock)',
            amountUsd: formatUsdFromDollars(Number(campaign.total_budget) || 0),
            emphasis: true,
        });
    }

    const hostFeeCents = !opts?.hasActivePlan && (opts?.perCampaignFeeCents ?? 0) > 0 ? opts!.perCampaignFeeCents! : 0;

    if (hostFeeCents > 0) {
        lines.push({
            label: 'Campaign host fee (no subscription)',
            amountUsd: hostFeeCents / 100,
            muted: true,
        });
    }

    const requiredWalletCents = escrowLockCents + hostFeeCents;
    const shortfallCents = Math.max(0, requiredWalletCents - Math.max(0, availableWalletCents));

    return {
        escrowLockCents,
        hostFeeCents,
        requiredWalletCents,
        shortfallCents,
        lines,
        isPaidCampaign,
        hostMinPayoutSubscribeToSavePerCreatorUsd,
    };
}

export function formatUsdCents(cents: number): string {
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
